/**
 * @fileoverview Article Opener Client Module for Esperit.cat
 * 
 * Copyright © 2025 ROWIKO SCP
 * All Rights Reserved
 * 
 * This code is proprietary and confidential. Unauthorized copying, transfer,
 * or reproduction of the contents of this software, via any medium, is strictly
 * prohibited. The receipt or possession of this software without explicit
 * authorization from ROWIKO SCP constitutes a violation of copyright law.
 * 
 * This software is licensed exclusively for use on Esperit.cat and associated
 * domains owned by ROWIKO SCP.
 * 
 * Module Description:
 * This module handles all client-side functionality for the article opener feature:
 * - UI state management for article access status
 * - Payment flow integration with Stripe
 * - Article status verification
 * - Payment session management
 * - Dynamic UI updates based on article state
 */

export default () => {
  apos.util.onReady(() => {
    /**
     * Updates the UI to reflect an open/purchased article state
     * - Changes the lock icon to open state
     * - Updates status text
     * - Hides payment options
     * - Disables dropdown functionality
     */
    const updateUIForOpenArticle = () => {
      const openerComponents = document.querySelectorAll('.dropdown.article-opener-component');
      
      openerComponents.forEach(opener => {
        const dropdownLabel = opener.querySelector('label[tabindex="0"]');
        const lockIcon = opener.querySelector('svg');
        const statusText = opener.querySelector('.article-status-text');
        const dropdownContent = opener.querySelector('.dropdown-content');
        
        // Update lock icon to show open state
        if (lockIcon) {
          lockIcon.innerHTML = '<path d="M 10.844,2.311 A 1.625,1.625 0 0 0 9.219,3.936 v 3.25 a 1.625,1.625 0 0 1 1.625,1.625 V 12.875 A 1.625,1.625 0 0 1 9.219,14.5 H 4.343 A 1.625,1.625 0 0 1 2.718,12.875 V 8.812 A 1.625,1.625 0 0 1 4.343,7.187 H 8.406 V 3.936 a 2.438,2.438 0 0 1 4.876,0 v 3.25 a 0.406,0.406 0 0 1 -0.813,0 V 3.936 a 1.625,1.625 0 0 0 -1.625,-1.625 z"/>';
          const currentClasses = lockIcon.getAttribute('class') || '';
          lockIcon.setAttribute('class', currentClasses.replace(/bg-\[[^\]]+\]/, 'bg-[#bcb12f]'));
        }

        // Update UI elements
        if (statusText) statusText.textContent = 'Article obert';
        if (dropdownContent) dropdownContent.classList.add('hidden');
        if (dropdownLabel) {
          dropdownLabel.style.cursor = 'default';
          dropdownLabel.removeAttribute('tabindex');
        }

        opener.classList.remove('dropdown');
      });
    };

    /**
     * Extracts the article slug from the current URL
     * @returns {string|undefined} The article slug or undefined if not found
     */
    const getArticleSlugFromUrl = () => {
      const pathParts = window.location.pathname.split('/');
      return pathParts[pathParts.length - 1];
    };

    /**
     * Checks the current article's state and updates UI accordingly
     * Makes API call to verify article status and purchase state
     * @returns {Promise<void>}
     */
    const checkArticleState = async () => {
      try {
        const articleSlug = getArticleSlugFromUrl();
        if (!articleSlug) return;

        const response = await apos.http.get(`/api/v1/article-opener/check-status/${articleSlug}`, {
          busy: true
        });

        if (response?.disponibilitat === 'public' || response?.hasPurchased) {
          updateUIForOpenArticle();
        }
      } catch (error) {
        console.error('Error checking article state:', error);
      }
    };

    /**
     * Verifies a completed payment session
     * - Validates the payment with the server
     * - Updates UI based on payment status
     * - Cleans up URL parameters
     * @param {string} sessionId - The Stripe session ID to verify
     * @returns {Promise<void>}
     */
    const verifyPayment = async (sessionId) => {
      try {
        const response = await apos.http.post('/api/v1/article-opener/verify-payment', {
          busy: true,
          body: { sessionId }
        });

        if (response.success) {
          // Clean up URL
          const url = new URL(window.location.href);
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url);
          
          // Update UI based on response
          if (response.alreadyPublic) {
            updateUIForOpenArticle();
          } else {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
      }
    };

    // Initialize article state only if on an article page (article opener component is present)
    if (document.querySelector('.article-opener-component')) checkArticleState();

    // Check for completed payment session
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) verifyPayment(sessionId);

    /**
     * Event handler for payment button clicks
     * - Creates a new Stripe payment session
     * - Redirects to Stripe Checkout
     * - Handles loading states and errors
     */
    document.addEventListener('click', async (event) => {
      if (event.target.matches('.article-opener-pay-button, .article-payment-button')) {
        const button = event.target;
        
        try {
          button.disabled = true;
          button.textContent = 'Carregant...';
          
          const articleSlug = getArticleSlugFromUrl();
          if (!articleSlug) throw new Error('Could not determine article slug');

          const response = await apos.http.post('/api/v1/article-opener/create-payment-session', {
            busy: true,
            body: { articleId: articleSlug }
          });

          if (response.url) {
            window.location.href = response.url;
          } else {
            throw new Error('No checkout URL in response');
          }
        } catch (error) {
          console.error('Error creating payment session:', error);
          button.textContent = 'Error - Try again';
          button.disabled = false;
        }
      }
    });
  });
}; 